import { useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Collapse,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format, parseJSON } from 'date-fns';
import { isEmpty } from 'lodash';
import { makeStyles } from 'makeStyles';

import { DATE_TIME_FORMAT } from 'common/constants';

import { DetailsButton } from 'commonComponents/DetailsButton';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';

import { LicenseDetails, LicenseFile, loadLicenseFileList } from 'api/licenses';
import { ExpandLessIcon, ExpandMoreIcon } from 'icons';
import { CircularProgressIcon } from 'icons/CircularProgressIcon';
import { creationDate, hasNoneHardwareBinding, licenseFileExpirationDate } from 'licenses/utils';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { useCurrentUserHasRole } from 'roles/helpers';
import { ROUTES } from 'routes/routes';

import { LicenseFileMoveButton } from './LicenseFileActions/LicenseFileMoveButton';

const useStyles = makeStyles()(theme => ({
    card: {
        marginTop: theme.spacing(1),
    },
    cardContent: {
        paddingTop: 0,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    cardHeader: {
        cursor: 'pointer',
    },
    rowExpired: {
        '& td': {
            color: theme.palette.error.main,
        },
    },
    row: {
        '& td': {
            color: theme.palette.text.primary,
        },
    },
    actionButtonCell: { textAlign: 'center', width: 0, paddingInline: 'inherit' },
    checkboxCell: { textAlign: 'center' },
    tableCellNoWrap: {
        whiteSpace: 'nowrap',
    },
}));

interface LicenseFilesCardProps {
    open: boolean;
    licenseFiles: LicenseFile[];
    license: LicenseDetails;
}

export function LicenseFilesCard({ open, licenseFiles, license }: LicenseFilesCardProps) {
    const { classes } = useStyles();
    const [selectedLicenseFiles, setSelectedLicenseFiles] = useState([]);
    const currentUserHasRole = useCurrentUserHasRole();
    const userIsAtLeastLicenseManager = currentUserHasRole(ROLE_DEFINITIONS.LicenseManager);
    const ellipsisSX = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    const canMoveLicense = useMemo(
        () => !license.invalidated && userIsAtLeastLicenseManager && license.configuration.product.allow_move_license,
        [license, userIsAtLeastLicenseManager]
    );

    function handleLicenseFileSelection(licenseFileId: number) {
        if (selectedLicenseFiles.includes(licenseFileId)) {
            setSelectedLicenseFiles(selectedLicenseFiles.filter(id => id !== licenseFileId));
        } else {
            setSelectedLicenseFiles([...selectedLicenseFiles, licenseFileId]);
        }
    }

    function clearLicenseFileSelection() {
        setSelectedLicenseFiles([]);
    }

    const licenseFileSelected = (licenseFileId: number) => {
        return selectedLicenseFiles.includes(licenseFileId);
    };

    const licenseFileRowClass = (licenseFile: LicenseFile) => {
        // Note: Styling for deactivated is only applied to some cells or even parts of a cell,
        // so this is done via the sx property where applicable.
        if (licenseFile.isExpired) {
            return classes.rowExpired;
        }
        return classes.row;
    };

    const licenseFileCellSx = (licenseFile: LicenseFile) =>
        licenseFile.deactivated ? { textDecoration: 'line-through' } : {};

    const buildCommentCell = (licenseFile: LicenseFile) => {
        return (
            <>
                <Box
                    title={licenseFile.comment}
                    sx={{ ...ellipsisSX, ...licenseFileCellSx(licenseFile), maxWidth: 100 }}
                >
                    {licenseFile.comment}
                </Box>
                {licenseFile.deactivated && (
                    <Box
                        title={licenseFile.deactivation_reason}
                        sx={{ ...ellipsisSX, maxWidth: 100, fontStyle: 'italic' }}
                    >
                        {licenseFile.deactivation_reason}
                    </Box>
                )}
            </>
        );
    };

    const buildStatusCell = (licenseFile: LicenseFile) => {
        if (licenseFile.deactivated) {
            return 'Deactivated';
        }
        if (licenseFile.lease_valid_to && licenseFile.lease_valid_to.length > 0) {
            const validToStr = licenseFile.lease_valid_to.map(v => format(parseJSON(v), DATE_TIME_FORMAT)).join('; ');
            const inUseStr = `In use (${licenseFile.lease_valid_to.length})`;
            return <Box title={`In use until ${validToStr}`}>{`${inUseStr}`}</Box>;
        }
        return '';
    };

    return (
        <Collapse in={open} timeout="auto" unmountOnExit>
            <Divider />
            <CardContent className={classes.cardContent}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {canMoveLicense && (
                                <TableCell className={classes.actionButtonCell}>
                                    <LicenseFileMoveButton
                                        license={license}
                                        licenseFileIds={selectedLicenseFiles}
                                        disabled={isEmpty(selectedLicenseFiles)}
                                        onSubmitCallback={clearLicenseFileSelection}
                                        title="Move"
                                        tooltip="Move selected license files to another license"
                                    />
                                </TableCell>
                            )}
                            {!hasNoneHardwareBinding(license.configuration) && <TableCell>System ID</TableCell>}
                            <TableCell className={classes.tableCellNoWrap}>Activation Date</TableCell>
                            <TableCell className={classes.tableCellNoWrap}>Expiration Date</TableCell>
                            <TableCell className={classes.tableCellNoWrap}>Support Expiration Date</TableCell>
                            <TableCell className={classes.tableCellNoWrap}>Comment</TableCell>
                            <TableCell className={classes.tableCellNoWrap}>Status</TableCell>
                            <TableCell sx={{ width: 30 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {licenseFiles.map(licenseFile => (
                            <TableRow
                                key={licenseFile.id}
                                className={licenseFileRowClass(licenseFile)}
                                data-testid={`licenseFileComment_${licenseFile.comment}`}
                            >
                                {canMoveLicense && (
                                    <TableCell className={classes.checkboxCell}>
                                        <Checkbox
                                            style={{ padding: 0 }}
                                            size="small"
                                            name={`select${licenseFile.id}`}
                                            checked={licenseFileSelected(licenseFile.id)}
                                            onClick={() => handleLicenseFileSelection(licenseFile.id)}
                                        />
                                    </TableCell>
                                )}
                                {!hasNoneHardwareBinding(license.configuration) && (
                                    <TableCell
                                        sx={{ ...licenseFileCellSx(licenseFile), ...ellipsisSX, maxWidth: 340 }}
                                        title={licenseFile.hardwareIdentifiers.join(', ')}
                                    >
                                        {licenseFile.hardwareIdentifiers.join(', ')}
                                    </TableCell>
                                )}
                                <TableCell className={classes.tableCellNoWrap} sx={licenseFileCellSx(licenseFile)}>
                                    {creationDate(licenseFile)}
                                </TableCell>
                                <TableCell className={classes.tableCellNoWrap} sx={licenseFileCellSx(licenseFile)}>
                                    {licenseFileExpirationDate(licenseFile.expiration_date)}
                                </TableCell>
                                <TableCell className={classes.tableCellNoWrap} sx={licenseFileCellSx(licenseFile)}>
                                    {licenseFileExpirationDate(licenseFile.support_expiration_date)}
                                </TableCell>
                                <TableCell>{buildCommentCell(licenseFile)}</TableCell>
                                <TableCell>{buildStatusCell(licenseFile)}</TableCell>
                                <TableCell>
                                    {!licenseFile.deactivated && (
                                        <DetailsButton
                                            route={generatePath(ROUTES.LICENSEFILE_DETAIL, {
                                                id: licenseFile.id.toString(),
                                            })}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Collapse>
    );
}

interface LicenseFilesProps {
    license: LicenseDetails;
    initiallyOpen: boolean;
}

export function LicenseFiles({ license, initiallyOpen }: LicenseFilesProps) {
    const { classes } = useStyles();
    const [open, setOpen] = useState(initiallyOpen ?? false);

    const { data: licenseFiles, error } = useQuery({
        queryKey: ['LicenseFile', 'list', { licenseId: license.id }],
        queryFn: () => loadLicenseFileList({ licenseId: license.id }),
        meta: { errorMessage: 'Loading license files failed' },
    });

    if (error) {
        return null;
    }

    return (
        <Card className={classes.card}>
            <CardHeader
                onClick={() => setOpen(licenseFiles && !open)}
                subheader="Activations"
                action={
                    <IconButton size="large">
                        {!licenseFiles ? <CircularProgressIcon /> : open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                }
                className={classes.cardHeader}
                data-testid="LicenseFilesCardHeader"
            >
                <LoadingIndicator />
            </CardHeader>
            {licenseFiles && <LicenseFilesCard open={open} licenseFiles={licenseFiles} license={license} />}
        </Card>
    );
}
